import { Text, View, Avatar, ActionIcon, CompanyLogo, ActivityIndicator, ProfileAvatarPickerModal } from '@/components'
import { theme, textVariant } from '@/config'
import { APIClient } from '@/services'
import { createStyles } from '@codeleap/styles'
import { PropsOf } from '@codeleap/types'
import { memoBy } from '@codeleap/utils'
import { useFileInput, CropPicker } from '@codeleap/web'
import React from 'react'

type ProfileBannerProps = {
  avatar?: any
  name?: string
  description?: string
  descriptionTestId?: string
  descriptionLoading?: boolean
  onAvatarChange?: PropsOf<typeof Avatar>['onChange']
  onRemove?: () => void
  logoShown?: boolean
  actions?: PropsOf<typeof ActionIcon>[]
}

export const ProfileBanner = memoBy((props: ProfileBannerProps) => {
  const {
    avatar,
    name,
    description,
    descriptionTestId,
    descriptionLoading = false,
    onAvatarChange,
    onRemove,
    actions = [],
    logoShown = true,
  } = props

  const branding = APIClient.Session.useCompanyBranding()
  const fileInput = useFileInput()

  const handleUploadPhoto = async () => {
    const files = await fileInput.openFilePicker()
    if (files.length) {
      onAvatarChange?.(files[0])
    }
  }

  const handleBadgePress = () => {
    ProfileAvatarPickerModal.open({
      hasAvatar: !!avatar,
      onUpload: handleUploadPhoto,
      onRemove,
    })
  }

  return (
    <View style={styles.wrapper}>
      <CropPicker
        debugName='ProfileBanner CropPicker'
        // @ts-expect-error
        ref={fileInput.ref}
        accept='image/png, image/jpeg, image/jpg'
      />
      <View style={styles.avatarWrapper}>
        <Avatar
          image={avatar}
          debugName='profile:avatar'
          badgeIcon={!onAvatarChange ? null : 'camera'}
          name={name?.toLocaleUpperCase()}
          firstNameOnly={false}
          onChange={undefined}
          style={{
            wrapper: styles.avatar,
            image: styles.avatar,
            badgeIconWrapper: [styles.avatarIcon, styles.badgeIconHoverArea, styles.badgeIconDisabled],
            touchable: { cursor: 'default', pointerEvents: 'none' },
            initials: styles.avatarInitials,
            cursor: 'default',
            pointerEvents: 'none',
          }}
        />
        {onAvatarChange && (
          <div
            className='avatar-badge-hover-area'
            style={styles.badgeHoverOverlay}
            onClick={handleBadgePress}
          />
        )}
      </View>

      <View style={['center', 'column', 'gap:1']}>
        <Text style={['h4', 'bold', { color: theme.colors.textHeadingHighlighted }]} text={name} />
        {descriptionLoading ? (
          <ActivityIndicator style={['small']} />
        ) : (
          !!description && <Text data-testid={descriptionTestId} style={['p3', 'regular', { color: theme.colors.textParagraph }]} text={description} />
        )}
      </View>

      {!!actions?.length && (
        <View style={['row', 'gap:2']}>
          {actions?.map(
            action => {
              return (
                <ActionIcon
                  key={action.debugName}
                  {...action}
                  style={['xx-large', 'regularTerciary']}
                />
              )
            },
          )}
        </View>
      )}

      <View style={[styles.bottomWrapper, logoShown && styles.logoWrapper]}>
        {!!logoShown && (
          <>

            <CompanyLogo />

            <Text style={['p3', 'regular', { color: theme.colors.textParagraph }]} text={branding.companyName} />
          </>
        )}
      </View>
    </View>
  )
}, ['name', 'description', 'descriptionLoading', 'avatar'])

const styles = createStyles(theme => ({
  wrapper: {
    ...theme.presets.fullWidth,
    ...theme.presets.column,
    ...theme.presets.center,
    overflow: 'hidden',
    ...theme.spacing.pt(3),
    ...theme.spacing.gap(3),
    borderRadius: theme.radius.regular,
    backgroundColor: '#F7F7F7',
  },
  bottomWrapper: {
    ...theme.presets.fullWidth,
    ...theme.presets.row,
    ...theme.presets.center,
    ...theme.spacing.py(1.5),
    ...theme.spacing.px(2),
    ...theme.spacing.gap(1),
  },
  logoWrapper: {
    backgroundColor: theme.baseColors.neutralSolid300,
  },
  iconWrapper: {
    ...theme.presets.center,
    backgroundColor: theme.baseColors.primarySolid500,
    borderRadius: 4,
    minWidth: theme.size.largeish,
    minHeight: theme.size.largeish,
    maxWidth: theme.size.largeish,
    maxHeight: theme.size.largeish,
  },
  icon: {
    aspectRatio: 99 / 101,
    width: 25,
  },
  avatar: {
    width: 120,
    height: 120,
  },
  avatarInitials: {
    ...textVariant('h1'),
    color: theme.baseColors.primarySolid50,
  },
  avatarIcon: {
    bottom: 0,
    top: 'auto',
    right: 0,
    left: 'auto',
    backgroundColor: theme.baseColors.primarySolid50,
  },
  avatarWrapper: {
    position: 'relative',
  },
  badgeHoverOverlay: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 40,
    height: 40,
    pointerEvents: 'auto',
    cursor: 'pointer',
  },
  badgeIconHoverArea: {
    cursor: 'pointer',
  },
  badgeIconDisabled: {
    pointerEvents: 'none',
  },
  actionMenuButton: {
    backgroundColor: 'transparent',
  },
}))
